/**
 * NavigationHistoryService — SMART NAVIGATION™ (Build 2.05.1).
 *
 * The single navigation authority for WorkRoamer™. Maintains a deterministic
 * route history stack so Back always returns to where the operator actually
 * came from — never to Dev Center or another unrelated page.
 *
 * Responsibilities
 *  • Push every route; ignore consecutive duplicates; cap at 100 entries
 *  • Detect browser/Android Back (new route === entry below top → pop)
 *  • Prevent A↔B history loops (alternation collapse)
 *  • Remember scroll position per entry and page state per route
 *  • Capture external departures (Google, Airbnb, Booking, Maps, jobs …)
 *    with route + scroll + timestamp + return token, restore on return
 *  • Survive refreshes and deep links via sessionStorage (session-scoped —
 *    a new tab starts a fresh, honest stack)
 *
 * No polling. No new architecture: wouter stays the router; the Shell simply
 * reports route changes here and asks where "back" is.
 */

const STACK_KEY = 'nomad:nav:history:v1';
const RETURN_KEY = 'nomad:nav:return:v1';
const PAGESTATE_KEY = 'nomad:nav:pagestate:v1';

export const MAX_HISTORY = 100;
/** External returns older than this are stale — do not restore. */
export const RETURN_MAX_AGE_MS = 6 * 60 * 60 * 1000; // 6 hours
/** A↔B alternations at/above this depth are collapsed as a loop. */
const LOOP_ALTERNATIONS = 3;
/** A popstate back-intent only applies to the immediately following route change. */
export const BACK_INTENT_TTL_MS = 5_000;

export interface NavEntry {
  route: string;
  scrollTop: number;
  at: number;
}

export interface ExternalDeparture {
  route: string;
  scrollTop: number;
  at: number;
  token: string;
  url: string;
}

export type NavAction = 'push' | 'pop' | 'duplicate' | 'loop-collapsed';

export interface NavDiagnostics {
  currentRoute: string | null;
  previousRoute: string | null;
  historyDepth: number;
  externalReturnPending: boolean;
  lastExternalUrl: string | null;
  loopsPrevented: number;
  lastScrollRestore: string | null;
  lastStateRestore: string | null;
}

function hasSession(): boolean {
  try { return typeof sessionStorage !== 'undefined'; } catch { return false; }
}

function readJSON<T>(key: string, fallback: T): T {
  if (!hasSession()) return fallback;
  try {
    const raw = sessionStorage.getItem(key);
    return raw != null ? JSON.parse(raw) as T : fallback;
  } catch { return fallback; }
}

function writeJSON(key: string, value: unknown): void {
  if (!hasSession()) return;
  try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* quota — best-effort */ }
}

function removeKey(key: string): void {
  if (!hasSession()) return;
  try { sessionStorage.removeItem(key); } catch { /* ignore */ }
}

function sanitizeStack(raw: unknown): NavEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((e): e is NavEntry =>
      e != null && typeof e === 'object' &&
      typeof (e as NavEntry).route === 'string' &&
      typeof (e as NavEntry).scrollTop === 'number' &&
      typeof (e as NavEntry).at === 'number')
    .slice(-MAX_HISTORY);
}

export class NavigationHistoryService {
  private stack: NavEntry[];
  private scrollTopGetter: (() => number) | null = null;
  private backIntentAt: number | null = null;
  private loopsPrevented = 0;
  private lastScrollRestore: string | null = null;
  private lastStateRestore: string | null = null;

  constructor() {
    // Refresh-safe: rehydrate the session's stack (deep links start fresh).
    this.stack = sanitizeStack(readJSON<unknown>(STACK_KEY, []));
  }

  private persist(): void {
    writeJSON(STACK_KEY, this.stack);
  }

  /** True when pushing `route` would extend an A↔B alternation loop. */
  private wouldLoop(route: string): boolean {
    const n = this.stack.length;
    if (n < LOOP_ALTERNATIONS * 2 - 1) return false;
    const other = this.stack[n - 1].route;
    if (other === route) return false;
    // Expect tail … route, other, route, other (alternating) — count pairs.
    let pairs = 0;
    for (let i = n - 1; i >= 1; i -= 2) {
      if (this.stack[i].route === other && this.stack[i - 1].route === route) pairs += 1;
      else break;
    }
    return pairs >= LOOP_ALTERNATIONS - 1;
  }

  /**
   * Signal that the NEXT route change is history-driven (browser/Android
   * Back fires `popstate`). Without this signal, navigating forward to the
   * previous route via a menu/link is a legitimate new visit — a push, not
   * a pop. The intent expires quickly so a stale signal can never
   * misclassify a later navigation.
   */
  markBackIntent(now: number = Date.now()): void {
    this.backIntentAt = now;
  }

  /**
   * Report a route change. Returns what the stack did:
   *  • 'duplicate'      — same route as top; nothing pushed
   *  • 'pop'            — history-driven Back (popstate intent) to the entry
   *                       below top — top popped
   *  • 'loop-collapsed' — an A↔B alternation was detected; only the
   *                       alternating tail is collapsed (earlier history
   *                       with the same route is untouched)
   *  • 'push'           — normal forward navigation
   */
  recordNavigation(route: string, now: number = Date.now()): NavAction {
    const backIntent = this.backIntentAt != null && now - this.backIntentAt < BACK_INTENT_TTL_MS;
    this.backIntentAt = null;
    const n = this.stack.length;
    if (n > 0 && this.stack[n - 1].route === route) {
      this.stack[n - 1].at = now;
      this.persist();
      return 'duplicate';
    }
    if (backIntent && n >= 2 && this.stack[n - 2].route === route) {
      this.stack.pop();
      this.stack[this.stack.length - 1].at = now;
      this.persist();
      return 'pop';
    }
    if (this.wouldLoop(route)) {
      // Collapse ONLY the alternating tail — walk the …route,other,route,
      // other pairs back from the top; earlier, unrelated visits of `route`
      // outside the tail are legitimate history and must survive.
      const other = this.stack[n - 1].route;
      let j = n - 1;
      while (j >= 1 && this.stack[j].route === other && this.stack[j - 1].route === route) j -= 2;
      const earliest = j + 1; // earliest `route` entry inside the tail
      if (earliest >= 0 && earliest < n && this.stack[earliest].route === route) {
        this.stack = this.stack.slice(0, earliest + 1);
        this.stack[this.stack.length - 1].at = now;
        this.loopsPrevented += 1;
        this.persist();
        return 'loop-collapsed';
      }
    }
    this.stack.push({ route, scrollTop: 0, at: now });
    if (this.stack.length > MAX_HISTORY) this.stack = this.stack.slice(-MAX_HISTORY);
    this.persist();
    return 'push';
  }

  /** Remember the scroll position of `route` (latest matching entry). */
  saveScroll(route: string, scrollTop: number): void {
    for (let i = this.stack.length - 1; i >= 0; i -= 1) {
      if (this.stack[i].route === route) {
        this.stack[i].scrollTop = scrollTop;
        this.persist();
        return;
      }
    }
  }

  /** Scroll position stored for the CURRENT top entry (used after 'pop'). */
  currentScrollTop(): number {
    const top = this.stack[this.stack.length - 1];
    return top ? top.scrollTop : 0;
  }

  markScrollRestored(route: string): void {
    this.lastScrollRestore = route;
  }

  canGoBack(): boolean {
    return this.stack.length >= 2;
  }

  currentRoute(): string | null {
    return this.stack[this.stack.length - 1]?.route ?? null;
  }

  previousRoute(): string | null {
    return this.stack[this.stack.length - 2]?.route ?? null;
  }

  /**
   * Universal Back — priority 1: the history stack. Pops the current entry
   * and returns the route to navigate to, or null when the stack has no
   * previous entry (callers then fall back to browser history, then Home).
   */
  back(): string | null {
    if (this.stack.length < 2) return null;
    this.stack.pop();
    this.persist();
    return this.stack[this.stack.length - 1].route;
  }

  // ── External departures ────────────────────────────────────────────────

  /** The Shell registers how to read the live scroll position of <main>. */
  registerScrollTopGetter(getter: (() => number) | null): void {
    this.scrollTopGetter = getter;
  }

  /** Call right before window.open(url) — remembers where we were. */
  captureExternalDeparture(url: string, scrollTop?: number, now: number = Date.now()): ExternalDeparture {
    let top = scrollTop ?? 0;
    if (scrollTop == null && this.scrollTopGetter != null) {
      try { top = this.scrollTopGetter(); } catch { top = 0; }
    }
    const departure: ExternalDeparture = {
      route: this.currentRoute() ?? '/home',
      scrollTop: top,
      at: now,
      token: `${now.toString(36)}-${Math.abs(url.length * 2654435761 % 0xffffff).toString(36)}`,
      url,
    };
    writeJSON(RETURN_KEY, departure);
    return departure;
  }

  /** Pending return, if fresh. Stale pending returns are cleared. */
  pendingReturn(now: number = Date.now()): ExternalDeparture | null {
    const raw = readJSON<ExternalDeparture | null>(RETURN_KEY, null);
    if (raw == null || typeof raw.route !== 'string' || typeof raw.at !== 'number') return null;
    if (now - raw.at > RETURN_MAX_AGE_MS) {
      removeKey(RETURN_KEY);
      return null;
    }
    return raw;
  }

  /** Consume (clear) the pending return and report it. */
  consumeReturn(now: number = Date.now()): ExternalDeparture | null {
    const pending = this.pendingReturn(now);
    removeKey(RETURN_KEY);
    return pending;
  }

  // ── Page state (expanded cards, tab selection, filters …) ─────────────

  setPageState(route: string, state: unknown): void {
    const all = readJSON<Record<string, unknown>>(PAGESTATE_KEY, {});
    all[route] = state;
    writeJSON(PAGESTATE_KEY, all);
  }

  getPageState<T>(route: string): T | null {
    const all = readJSON<Record<string, unknown>>(PAGESTATE_KEY, {});
    if (!(route in all)) return null;
    this.lastStateRestore = route;
    return all[route] as T;
  }

  // ── Diagnostics / lifecycle ────────────────────────────────────────────

  getDiagnostics(): NavDiagnostics {
    const pending = this.pendingReturn();
    return {
      currentRoute: this.currentRoute(),
      previousRoute: this.previousRoute(),
      historyDepth: this.stack.length,
      externalReturnPending: pending != null,
      lastExternalUrl: pending?.url ?? readJSON<ExternalDeparture | null>(RETURN_KEY, null)?.url ?? null,
      loopsPrevented: this.loopsPrevented,
      lastScrollRestore: this.lastScrollRestore,
      lastStateRestore: this.lastStateRestore,
    };
  }

  getStack(): NavEntry[] {
    return this.stack.map((e) => ({ ...e }));
  }

  reset(): void {
    this.stack = [];
    this.backIntentAt = null;
    this.loopsPrevented = 0;
    this.lastScrollRestore = null;
    this.lastStateRestore = null;
    removeKey(STACK_KEY);
    removeKey(RETURN_KEY);
    removeKey(PAGESTATE_KEY);
  }
}

export const navigationHistory = new NavigationHistoryService();
